import { _throwNotImplementedException } from './COM';
import { ReachRs232Controller } from './ReachPlayerRs232Controller';
import { BrightSignRs232Controller } from './BrightSignRs232Controller';
import { Signage } from '../../Signage';
import * as Platform from '..';

// TODO: move `REACH` calls into `ReachPlayerRs232Controller`.
/* global REACH */
/* global BSSerialPort */

/**
 * Generic Rs232Controller
 * @class
 * @param {string} comPort - the name of the COM port to open
 * @param {ComSettings} comSettings - settings for the
 * com port
 */
function Rs232Controller(comPort, comSettings) {
    comSettings = comSettings || {};

    /**
     * the COM port to use
     * @type {string}
     */
    this.comPort = comPort;

    /**
     * Baud rate for the connection
     * @type {integer}
     */
    this.baudRate = comSettings.baudRate || 9600;

    /**
     * Data bits for the connection
     * @type {integer}
     */
    this.dataBits = comSettings.dataBits || 8;

    /**
     * Stop bits for the connection. Any of None, One, OnePointFive, Two
     * @type {string}
     */
    this.stopBits = comSettings.stopBits || 'One';

    /**
     * parity for the connection. Must be one of 'Even', 'Mark', 'None', 'Odd',
     * 'Space'
     * @type {('Even'|'Mark'|'None'|'Odd'|'Space')}
     */
    this.parity = comSettings.parity || 'None';

    /**
     * discard null for the connection.
     * @type {string}
     */
    this.discardNull = comSettings.discardNull || false;

    /**
     * encoding for the connection.
     * @type {string}
     */
    this.encoding = comSettings.encoding || 'ASCII';

    /**
     * handshake for the connection. One of None, RequestToSend,
     * RequestToSendXOnXOff, XOnXOff
     * @type {string}
     */
    this.handshake = comSettings.handshake || 'None';

    /**
     * newline character for the connection.
     * @type {char}
     */
    this.newLine = comSettings.newLine || String.fromCharCode(0x0d);

    /**
     * read buffer size for the connection.
     * @type {integer}
     */
    this.readBufferSize = comSettings.readBufferSize || 4096;

    /**
     * read time out for the connection.
     * @type {integer}
     */
    this.readTimeout = comSettings.readTimeout || 500;

    /**
     * read bytes threshold
     * @type {integer}
     */
    this.readBytesThreshold = comSettings.readBytesThreshold || 1;

    /**
     * DTR enabled
     * @type {integer}
     */
    this.dtrEnabled = comSettings.dtrEnabled || false;

    /**
     * RTS enabled
     * @type {integer}
     */
    this.rtsEnabled = comSettings.rtsEnabled || false;

    /**
     * Write buffer size
     * @type {integer}
     */
    this.writeBufferSize = comSettings.writeBufferSize || 2048;

    /**
     * Write timeout
     * @type {integer}
     */
    this.writeTimeout = comSettings.writeTimeout || 500;
}

/**
 * opens a connection to the COM port using internal settings
 * @method
 * @throws will throw error if not overridden in implementing class
 */
Rs232Controller.prototype.open = _throwNotImplementedException('open');

/**
 * closes a connection to the COM port
 * @method
 * @throws will throw error if not overridden in implementing class
 */
Rs232Controller.prototype.close = _throwNotImplementedException('close');

/**
 * writes an array of bytes to the COM port
 * @method
 * @param {Uint8Array} data - the data to write to the port
 * @throws will throw error if not overridden in implementing class
 */
Rs232Controller.prototype.write = _throwNotImplementedException('write');

/**
 * writes an array of bytes to the COM port with an newLine character
 * @method
 * @param {Uint8Array} data - the data to write to the port
 * @throws will throw error if not overridden in implementing class
 */
Rs232Controller.prototype.writeLine = _throwNotImplementedException('writeLine');

/**
 * reads numBytes bytes from the COM port
 * @method
 * @param {number} numBytes - the number of bytes to read from the COM port
 * @returns {Promise<int[]>}
 *      Success `Promise[int[]]`  <br />
 *      Failure `Promise`
 * @throws will throw error if not overridden in implementing class
 */
Rs232Controller.prototype.read = _throwNotImplementedException('read');

/**
 * reads bytes from the COM port until newLine is encountered
 * @method
 * @returns {Promise<int[]>}
 *      Success `Promise[int[]]`  <br />
 *      Failure `Promise`
 * @throws will throw error if not overridden in implementing class
 */
Rs232Controller.prototype.readLine = _throwNotImplementedException('readLine');

/**
 * creates a platform-specific RS232 controller concrete instance on comPort COM
 * port using comSettings settings
 * @static
 * @returns {Rs232Controller}
 * @throws will throw error if no RS232 controller is available on the platform
 */
Rs232Controller.createRs232Controller = function (comPort, comSettings) {
    switch (Signage.platform.type) {
        case Platform.REACH_PLAYER:
            return new ReachRs232Controller(comPort, comSettings);
        case Platform.BRIGHTSIGN:
            return new BrightSignRs232Controller(comPort, comSettings);
        default:
            _throwNotImplementedException('UnknownRs232Controller')();

            break;
    }
};

/**
 * Enumerates all the COM ports on the system
 * @static
 * @returns {string[]}
 * @throws will throw Error if COM ports cannot be controlled on the platform
 */
Rs232Controller.enumerateComPorts = function () {
    switch (Signage.platform.type) {
        case Platform.REACH_PLAYER:
            return REACH.ComPort.EnumerateComPorts();
        case Platform.BRIGHTSIGN:
            // NOTE: not all brightsign units will use all 6 serial ports
            // brightsign says can be used.
            var possiblePorts = [0, 1, 2, 3, 4, 5];
            var portsFound = [];

            for (var port of possiblePorts) {
                try {
                    var bsPort = new BSSerialPort(port);
                    bsPort.close(); // clean up resources
                    // no exception means the port was found
                    portsFound.push(port);
                } catch (e) {
                    // throws an exception when the port does not exist. do not
                    // add the port to the list of available ports
                }
            }

            return portsFound;
        default:
            _throwNotImplementedException('Rs232Controller.enumerateComPorts~Unknown')();

            break;
    }
};

export { Rs232Controller };
